import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd, { getWebPageSchema, getBreadcrumbSchema, getOrganizationSchema } from '@/components/feature/JsonLd';
import useScrollToTop from '@/hooks/useScrollToTop';
import { movies } from '@/mocks/movies';
import { siteInfo } from '@/mocks/siteInfo';

export default function DetailPage() {
  useScrollToTop();
  const { id } = useParams<{ id: string }>();

  const movie = useMemo(() => {
    return movies.find(m => m.id === id);
  }, [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter(m => m.id !== movie.id && (m.categorySlug === movie.categorySlug || m.genre.some(g => movie.genre.includes(g))))
      .slice(0, 6);
  }, [movie]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-900 mb-2">影片未找到</h2>
            <p className="text-foreground-500 mb-4">抱歉，您要查找的影片不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${movie.title} | 4K超清在线观看 | ${siteInfo.name}`;
  const pageDesc = `${movie.title}，${movie.year}年${movie.region}${movie.category}，豆瓣${movie.rating}分。导演${movie.director}，主演${movie.actors.join('、')}。${movie.description}。高清4K画质免费在线播放，流畅不缓冲。`;

  return (
    <>
      <JsonLd {...getOrganizationSchema()} />
      <JsonLd {...getWebPageSchema(pageTitle, pageDesc, `${siteInfo.domain}/detail/${movie.id}`)} />
      <JsonLd {...getBreadcrumbSchema([
        { name: '首页', url: siteInfo.domain },
        { name: movie.category, url: `${siteInfo.domain}/category/${movie.categorySlug}` },
        { name: movie.title, url: `${siteInfo.domain}/detail/${movie.id}` },
      ])} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="relative h-[280px] md:h-[400px] overflow-hidden">
            <img
              src={movie.backdrop}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent"></div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-32 relative z-10">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-500 transition-colors">{movie.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900">{movie.title}</span>
            </div>

            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-40 md:w-56 mx-auto lg:mx-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} - 4K电影高清画质在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1 pt-2">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary-500 text-background-50 rounded whitespace-nowrap">{movie.quality}</span>
                  {movie.isNew && <span className="px-2 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded whitespace-nowrap">NEW</span>}
                  {movie.isHot && <span className="px-2 py-0.5 text-xs font-medium bg-secondary-500 text-background-50 rounded whitespace-nowrap">热播</span>}
                  <span className="px-2 py-0.5 text-xs bg-background-100 text-foreground-600 rounded">{movie.category}</span>
                </div>

                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                  {movie.title}
                  <span className="text-base font-normal text-foreground-500 ml-2">{movie.englishTitle}</span>
                </h1>

                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="flex items-center gap-1 text-lg font-bold text-primary-500">
                    <i className="ri-star-fill text-primary-400"></i>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">
                    {movie.views.toLocaleString()} 次播放
                  </span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-sm text-foreground-500">|</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {movie.genre.map(g => (
                    <Link
                      key={g}
                      to={`/category/${movie.categorySlug}`}
                      className="px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors"
                    >
                      {g}
                    </Link>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-4 max-w-2xl">
                  {movie.description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap w-12">导演：</span>
                    <span className="text-sm text-foreground-900">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap w-12">主演：</span>
                    <span className="text-sm text-foreground-900">{movie.actors.join(' / ')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap w-12">地区：</span>
                    <span className="text-sm text-foreground-900">{movie.region}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-foreground-500 whitespace-nowrap w-12">语言：</span>
                    <span className="text-sm text-foreground-900">国语</span>
                  </div>
                  {movie.episodeTotal && (
                    <div className="flex items-start gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap w-12">集数：</span>
                      <span className="text-sm text-foreground-900">
                        {movie.episodeCurrent === movie.episodeTotal
                          ? `共${movie.episodeTotal}集（已完结）`
                          : `更新至${movie.episodeCurrent}/${movie.episodeTotal}集`}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                    <i className="ri-play-fill text-lg"></i>
                    立即播放
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-heart-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer">
                    <i className="ri-share-forward-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">剧情简介</h2>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                    {movie.longDescription}
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4">相关推荐</h2>
                <div className="space-y-3">
                  {relatedMovies.map(rm => (
                    <Link
                      key={rm.id}
                      to={`/detail/${rm.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-16 h-20 flex-shrink-0 rounded overflow-hidden bg-background-200">
                        <img src={rm.poster} alt={rm.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                          {rm.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-400 text-[10px]"></i>
                            {rm.rating}
                          </span>
                          <span>{rm.year}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="pb-14"></div>
        </main>

        <Footer />
      </div>
    </>
  );
}